import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { FaGithub, FaGitlab, FaDocker } from 'react-icons/fa';
import type { IconType } from 'react-icons';
import {
  ArchiveBoxIcon, ArrowPathIcon, ClockIcon, GlobeAltIcon,
  ChartBarIcon, InformationCircleIcon, ExclamationTriangleIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon, XCircleIcon, StopCircleIcon } from '@heroicons/react/24/solid';

import { useTranslation } from 'react-i18next';
import RepositoryConnectorV2 from '../components/RepositoryConnectorV2';
import BuildProgressModal from '../components/BuildProgressModal';
import DeployBuildModal from '../components/DeployBuildModal';
import { BuildConfigList } from '../components/BuildConfig';
import { Modal } from '../components/ui';
import ImportProgressModal from '../components/ImportProgressModal';
import BuiltImagesPage from './BuiltImagesPage';
import apiService from '../services/api';
import Logger from '../utils/logger';
import { ConnectedRepository, ValidateImageRequest, ValidateImageResponse, RegistryCredentialItem, Project } from '../types/api';

const RIcon: React.FC<{ icon: IconType; className?: string }> = ({ icon, className }) => {
  const Icon = icon as React.ComponentType<{ className?: string }>;
  return <Icon className={className} />;
};

/** Ce que la mise en service AUTOMATIQUE a fait, tel que l'API le consigne sur le build.
 *
 *  `undefined`/`null` = jamais tentee (build en cours, ou anterieur au 2026-10-12), et
 *  l'ecran se tait alors. Un etat `sans_cible` dit au contraire « tentee, et voici
 *  pourquoi rien n'est parti » : c'est le cas le plus facile a prendre pour une panne.
 */
interface VerdictLivraison {
  etat: 'livre' | 'partiel' | 'inchange' | 'sans_cible' | 'echec';
  le?: string;
  message?: string;
  raison?: string;
  cibles?: Array<{
    deployment_id?: string;
    nom?: string;
    statut?: string;
    raison?: string | null;
    message?: string | null;
    reessayable?: boolean | null;
  }>;
}

interface BuildHistoryItem {
  buildId: string;
  repoName: string;
  repoOwner: string;
  branch: string;
  provider: string;
  startedAt: string;
  status: 'running' | 'pending' | 'success' | 'succeeded' | 'failed' | 'cancelled';
  /** La SOURCE du build, rendue par l'API depuis le 2026-10-11. `null` sur les builds
   *  anterieurs : sans elle on ne sait pas quelles cibles proposer, donc pas de bouton. */
  connectedRepositoryId?: number | null;
  /** Le verdict de la mise en service automatique. Porte par la LISTE, pas par la fiche :
   *  aller chercher ce champ build par build rouvrirait la saturation du pool du
   *  2026-09-13. */
  livraisonAuto?: VerdictLivraison | null;
  imageUrl?: string | null;
  imageTag?: string | null;
  duration?: number; // seconds, calculated from started_at/completed_at
}

/** La couleur d'un verdict. `echec` et `partiel` sont les deux seuls etats alarmants :
 *  `sans_cible` est le plus souvent une configuration (personne ne suit ce depot, ou pas
 *  sur cette branche), pas une panne -- le peindre en rouge ferait ouvrir des tickets. */
const verdictClasse = (etat: string): string => {
  switch (etat) {
    case 'livre': return 'bg-green-500/15 text-green-300';
    case 'partiel': return 'bg-orange-500/15 text-orange-300';
    case 'echec': return 'bg-red-500/15 text-red-300';
    case 'inchange': return 'bg-dark-600/60 text-gray-300';
    default: return 'bg-dark-600/60 text-gray-400';
  }
};

const verdictEstMauvais = (etat: string): boolean => etat === 'echec' || etat === 'partiel';

const BuildsPage: React.FC = () => {
  const { t } = (useTranslation as any)('builds');
  const [connectedRepos, setConnectedRepos] = useState<ConnectedRepository[]>([]);
  const [loading, setLoading] = useState(true);
  const [showConnectRepoModal, setShowConnectRepoModal] = useState(false);
  const [showBuildProgress, setShowBuildProgress] = useState(false);
  const [currentBuildId, setCurrentBuildId] = useState<string | null>(null);
  const [buildingRepoId, setBuildingRepoId] = useState<number | null>(null);
  const [buildHistory, setBuildHistory] = useState<BuildHistoryItem[]>([]);
  const [activeTab, setActiveTab] = useState<'repos' | 'history' | 'public' | 'import' | 'registry'>('repos');

  // Import image states
  const [importSourceImage, setImportSourceImage] = useState('');
  const [importTargetName, setImportTargetName] = useState('');
  const [importTargetTag, setImportTargetTag] = useState('latest');
  const [importingImage, setImportingImage] = useState(false);
  const [showImportProgress, setShowImportProgress] = useState(false);
  const [currentImportId, setCurrentImportId] = useState<string | null>(null);

  // Import credentials (optional, for private images)
  const [importNeedsAuth, setImportNeedsAuth] = useState(false);
  const [importUsername, setImportUsername] = useState('');
  const [importPassword, setImportPassword] = useState('');
  const [importRegistryUrl, setImportRegistryUrl] = useState('');

  // Validation state
  const [validatingImage, setValidatingImage] = useState(false);
  const [imageValidation, setImageValidation] = useState<ValidateImageResponse | null>(null);

  // Credential verification & stored credentials
  const [verifyingCredentials, setVerifyingCredentials] = useState(false);
  const [credentialValidation, setCredentialValidation] = useState<{valid: boolean; message: string; credential_id?: string} | null>(null);
  const [storedCredentials, setStoredCredentials] = useState<RegistryCredentialItem[]>([]);
  const [loadingCredentials, setLoadingCredentials] = useState(false);
  const [selectedCredentialId, setSelectedCredentialId] = useState<string | null>(null);
  const [revalidatingId, setRevalidatingId] = useState<string | null>(null);
  const [deletingCredentialId, setDeletingCredentialId] = useState<string | null>(null);

  // Project scope selector
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  const [publicRepoUrl, setPublicRepoUrl] = useState('');
  const [publicRepoBranch, setPublicRepoBranch] = useState('main');
  const [publicImageTag, setPublicImageTag] = useState('latest');
  const [publicSourceSubpath, setPublicSourceSubpath] = useState('');
  const [buildingPublicRepo, setBuildingPublicRepo] = useState(false);

  // State for branch selection modal
  const [showBranchSelectModal, setShowBranchSelectModal] = useState(false);
  const [selectedRepoForBuild, setSelectedRepoForBuild] = useState<ConnectedRepository | null>(null);
  const [buildBranch, setBuildBranch] = useState('main');
  const [buildImageTag, setBuildImageTag] = useState('latest');
  const [sourceSubpath, setSourceSubpath] = useState('');
  const [availableBranches, setAvailableBranches] = useState<Array<{ name: string; is_default: boolean }>>([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [branchesError, setBranchesError] = useState<string | null>(null);

  // State for build configuration modal
  const [showBuildConfigModal, setShowBuildConfigModal] = useState(false);
  const [selectedRepoForConfig, setSelectedRepoForConfig] = useState<ConnectedRepository | null>(null);

  // Multi-select for bulk deletion
  const [selectedBuildIds, setSelectedBuildIds] = useState<Set<string>>(new Set());
  // La mise en service d'un build termine. On garde l'ITEM entier : le modal a besoin
  // de la source et de l'image, pas seulement de l'identifiant.
  const [buildADeployer, setBuildADeployer] = useState<BuildHistoryItem | null>(null);
  const [deletingSelected, setDeletingSelected] = useState(false);

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    loadConnectedRepositories();
    loadBuildHistory();
  }, [selectedProjectId]);

  // Handle OAuth callback - check if we're returning from OAuth flow
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const state = params.get('state');
    const provider = params.get('provider');

    if (state && provider) {
      // We're returning from OAuth callback, open the modal
      setShowConnectRepoModal(true);
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const loadProjects = async () => {
    try {
      const data = await apiService.getProjects();
      setProjects(data);
    } catch (error) {
      Logger.error('Erreur lors du chargement des projets:', error);
    }
  };

  const loadConnectedRepositories = async () => {
    try {
      setLoading(true);
      const repos = await apiService.getConnectedRepositories(selectedProjectId || undefined);
      setConnectedRepos(repos);
    } catch (error: unknown) {
      Logger.error('Erreur lors du chargement des repositories:', error);
      toast.error(t('loadError'));
    } finally {
      setLoading(false);
    }
  };

  const loadBuildHistory = async () => {
    try {
      const response = await apiService.getUserBuilds(selectedProjectId || undefined);
      // Convert API response to BuildHistoryItem format
      const builds: BuildHistoryItem[] = response.builds.map((build: { app_name?: string; build_id: string; created_at: string; started_at?: string; completed_at?: string; status: string; git_branch?: string | null; connected_repository_id?: number | null; image_url?: string | null; image_tag?: string | null; livraison_auto?: VerdictLivraison | null }) => {
        // Extract repo info from app_name or build_id
        const repoName = build.app_name || 'unknown';
        const repoOwner = 'user'; // We don't have this info in DB yet

        const started = build.started_at ? new Date(build.started_at) : null;
        const completed = build.completed_at ? new Date(build.completed_at) : null;
        const duration = started && completed ? Math.round((completed.getTime() - started.getTime()) / 1000) : undefined;

        return {
          buildId: build.build_id,
          repoName: repoName,
          repoOwner: repoOwner,
          // La vraie branche, rendue par l'API. `null` sur les builds anterieurs au
          // 2026-10-11 : on affiche alors un tiret plutot que d'inventer « main »,
          // ce que faisait cette ligne et qui devenait faux des qu'on construisait
          // une autre branche.
          branch: build.git_branch || '-',
          provider: 'GitHub', // We don't store this yet
          connectedRepositoryId: build.connected_repository_id ?? null,
          livraisonAuto: build.livraison_auto ?? null,
          imageUrl: build.image_url ?? null,
          imageTag: build.image_tag ?? null,
          startedAt: build.started_at || build.created_at,
          status: build.status as 'running' | 'pending' | 'success' | 'failed',
          duration,
        };
      });
      setBuildHistory(builds);
    } catch (error) {
      Logger.error('Error loading build history:', error);
      // Fallback to empty array on error
      setBuildHistory([]);
    }
  };

  const deleteBuildFromHistory = async (buildId: string) => {
    try {
      await apiService.deleteBuild(buildId);
      toast.success(t('buildDeleted'));
      loadBuildHistory();
    } catch (error) {
      Logger.error('Error deleting build:', error);
      toast.error(t('buildDeleteError'));
    }
  };

  const deleteSelectedBuilds = async () => {
    if (selectedBuildIds.size === 0) return;
    if (!window.confirm(t('deleteBuildsConfirm', { count: selectedBuildIds.size }) as string)) return;

    setDeletingSelected(true);
    // Delete sequentially to avoid exhausting the DB connection pool
    const ids = Array.from(selectedBuildIds);
    let failed = 0;
    for (const id of ids) {
      try {
        await apiService.deleteBuild(id);
      } catch {
        failed++;
      }
    }
    if (failed > 0) {
      toast.error(t('deletionsError', { count: failed }));
    } else {
      toast.success(t('buildsDeleted', { count: selectedBuildIds.size }));
    }
    setSelectedBuildIds(new Set());
    setDeletingSelected(false);
    loadBuildHistory();
  };

  const toggleBuildSelection = (buildId: string) => {
    setSelectedBuildIds((prev) => {
      const next = new Set(prev);
      if (next.has(buildId)) {
        next.delete(buildId);
      } else {
        next.add(buildId);
      }
      return next;
    });
  };

  const terminalStatuses = ['success', 'succeeded', 'failed', 'cancelled'];
  const selectableBuilds = buildHistory.filter((b) => terminalStatuses.includes(b.status));

  const toggleSelectAll = () => {
    if (selectedBuildIds.size === selectableBuilds.length && selectableBuilds.length > 0) {
      setSelectedBuildIds(new Set());
    } else {
      setSelectedBuildIds(new Set(selectableBuilds.map((b) => b.buildId)));
    }
  };

  const updateBuildStatus = (buildId: string, status: string) => {
    // Update local state immediately for responsiveness
    const updatedHistory = buildHistory.map(build =>
      build.buildId === buildId
        ? { ...build, status: status as 'running' | 'pending' | 'success' | 'failed' }
        : build
    );
    setBuildHistory(updatedHistory);
  };

  const handleStartBuild = async (repo: ConnectedRepository) => {
    if (!selectedProjectId) {
      toast.error(t('selectProjectBeforeBuild'));
      return;
    }

    // Show branch selection modal and load branches
    setSelectedRepoForBuild(repo);
    setBuildBranch(repo.monitored_branch || repo.default_branch || 'main');
    setAvailableBranches([]);
    setBranchesError(null);
    setLoadingBranches(true);
    setShowBranchSelectModal(true);

    try {
      const data = await apiService.getRepositoryBranches(repo.id);
      setAvailableBranches(data.branches || []);

      // Pre-select the monitored/default branch
      if (data.branches && data.branches.length > 0) {
        const defaultBranch = data.branches.find((b: { name: string; is_default: boolean }) => b.is_default)?.name ||
                            data.default_branch ||
                            repo.monitored_branch ||
                            repo.default_branch ||
                            data.branches[0].name;
        setBuildBranch(defaultBranch);
      }
    } catch (error: unknown) {
      Logger.error('Error loading branches:', error);
      setBranchesError(t('branchLoadError'));
      // Keep the modal open so user can type manually
    } finally {
      setLoadingBranches(false);
    }
  };

  const handleConfirmBuild = async () => {
    if (!selectedRepoForBuild || !buildBranch.trim()) {
      toast.error(t('repoAndBranchRequired'));
      return;
    }

    try {
      setBuildingRepoId(selectedRepoForBuild.id);
      setShowBranchSelectModal(false);

      Logger.log('Starting build for repository:', selectedRepoForBuild.id);

      // Start the build via API using the repository information with the selected branch
      // For connected repos, we need to provide the OAuth token which is already stored
      const buildResponse = await apiService.launchIntelligentBuild({
        repository_url: selectedRepoForBuild.clone_url,
        git_provider: selectedRepoForBuild.oauth_provider.name.toLowerCase() as 'github' | 'gitlab',
        git_token: '', // Token is already stored in backend for connected repos
        branch: buildBranch,
        image_tag: buildImageTag || 'latest',
        source_subpath: sourceSubpath || undefined,
        project_id: selectedProjectId || undefined
      });

      if (buildResponse.build_id) {
        setCurrentBuildId(buildResponse.build_id);
        setShowBuildProgress(true);
        toast.success(t('buildStarted'));

        // Add the new build to the history immediately for responsiveness
        const newBuild: BuildHistoryItem = {
          buildId: buildResponse.build_id,
          repoName: selectedRepoForBuild.repo_name || selectedRepoForBuild.clone_url.split('/').pop() || 'unknown',
          repoOwner: selectedRepoForBuild.repo_owner || 'user',
          branch: buildBranch,
          provider: selectedRepoForBuild.oauth_provider.name,
          startedAt: new Date().toISOString(),
          status: 'pending'
        };

        // Add new build to the top of history
        setBuildHistory([newBuild, ...buildHistory]);

        // Reload build history in the background to sync with API
        loadBuildHistory();
      }
    } catch (error: unknown) {
      Logger.error('Erreur lors du démarrage du build:', error);

      // Extract error message from the response
      let errorMessage = t('buildStartError');

      const apiError = error as { response?: { data?: { detail?: string | { message?: string; error?: string } } } };
      if (apiError.response?.data?.detail) {
        const detail = apiError.response.data.detail;
        // If detail is an object (BuildErrorResponse), extract the message
        if (typeof detail === 'object' && detail.message) {
          errorMessage = detail.message;
          if (detail.error) {
            errorMessage += `: ${detail.error}`;
          }
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }

      toast.error(errorMessage);
    } finally {
      setBuildingRepoId(null);
      setSelectedRepoForBuild(null);
      setSourceSubpath('');
    }
  };

  const handleOpenBuildConfig = (repo: ConnectedRepository) => {
    setSelectedRepoForConfig(repo);
    setShowBuildConfigModal(true);
  };

  const handleCloseBuildConfig = () => {
    setShowBuildConfigModal(false);
    setSelectedRepoForConfig(null);
  };

  const handleDeleteRepo = async (repoId: number) => {
    if (!window.confirm(t('repoDeleteConfirm') as string)) {
      return;
    }

    try {
      await apiService.disconnectRepository(repoId);
      toast.success(t('repoDeleted'));
      await loadConnectedRepositories();
    } catch (error: unknown) {
      Logger.error('Erreur lors de la suppression:', error);
      toast.error(t('repoDeleteError'));
    }
  };

  const handleBuildPublicRepo = async () => {
    if (!selectedProjectId) {
      toast.error(t('selectProjectBeforeBuild'));
      return;
    }

    if (!publicRepoUrl.trim()) {
      toast.error(t('enterRepoUrl'));
      return;
    }

    // Validate URL format
    if (!publicRepoUrl.match(/^https?:\/\//)) {
      toast.error(t('urlMustStartWith'));
      return;
    }

    try {
      setBuildingPublicRepo(true);

      const buildResponse = await apiService.launchIntelligentBuild({
        repository_url: publicRepoUrl,
        git_provider: publicRepoUrl.includes('github') ? 'github' : 'gitlab',
        git_token: '', // No token for public repos
        branch: publicRepoBranch,
        image_tag: publicImageTag || 'latest',
        source_subpath: publicSourceSubpath || undefined,
        project_id: selectedProjectId || undefined
      });

      if (buildResponse.build_id) {
        setCurrentBuildId(buildResponse.build_id);
        setShowBuildProgress(true);
        toast.success(t('buildStarted'));

        // Add the new build to the history immediately for responsiveness
        const newBuild: BuildHistoryItem = {
          buildId: buildResponse.build_id,
          repoName: publicRepoUrl.split('/').pop() || publicRepoUrl,
          repoOwner: 'public',
          branch: publicRepoBranch,
          provider: publicRepoUrl.includes('github') ? 'GitHub' : 'GitLab',
          startedAt: new Date().toISOString(),
          status: 'pending'
        };

        // Add new build to the top of history
        setBuildHistory([newBuild, ...buildHistory]);

        // Reset form
        setPublicRepoUrl('');
        setPublicRepoBranch('main');
        setPublicImageTag('latest');
        setPublicSourceSubpath('');

        // Reload build history in the background to sync with API
        loadBuildHistory();
      }
    } catch (error: unknown) {
      Logger.error('Erreur lors du démarrage du build:', error);

      let errorMessage = t('buildStartError');

      const apiError = error as { response?: { data?: { detail?: string | { message?: string; error?: string } } } };
      if (apiError.response?.data?.detail) {
        const detail = apiError.response.data.detail;
        if (typeof detail === 'object' && detail.message) {
          errorMessage = detail.message;
          if (detail.error) {
            errorMessage += `: ${detail.error}`;
          }
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }

      toast.error(errorMessage);
    } finally {
      setBuildingPublicRepo(false);
    }
  };

  // ============================================================================
  // IMAGE IMPORT HANDLERS
  // ============================================================================

  const handleValidateImage = async () => {
    if (!importSourceImage.trim()) {
      toast.error(t('enterImageRef'));
      return;
    }

    try {
      setValidatingImage(true);
      setImageValidation(null);

      // Construit le payload :
      //  - si une credential stockée est sélectionnée, envoyer credential_id (+ project_id
      //    pour la lookup RBAC côté back) — le back décrypte le password lui-même,
      //    jamais exposé au front.
      //  - sinon fallback inline (form auth "one-shot").
      const payload: ValidateImageRequest = { source_image: importSourceImage };
      if (selectedCredentialId) {
        payload.credential_id = selectedCredentialId;
        if (selectedProjectId) {
          payload.project_id = selectedProjectId;
        }
      } else if (importNeedsAuth && importUsername && importPassword) {
        payload.credentials = {
          registry_url: importRegistryUrl,
          username: importUsername,
          password: importPassword,
        };
      }

      const result = await apiService.validateImage(payload);

      setImageValidation(result);

      // Auto-fill target name from source image
      if (result.exists) {
        // Extract name: "nginx:1.25" -> "nginx", "ghcr.io/user/app:v2" -> "app"
        const parts = importSourceImage.split(':');
        const imagePath = parts[0];
        const tag = parts[1] || 'latest';
        const nameParts = imagePath.split('/');
        const name = nameParts[nameParts.length - 1]
          .toLowerCase()
          .replace(/[^a-z0-9-]/g, '-');

        if (!importTargetName) {
          setImportTargetName(name);
        }
        if (importTargetTag === 'latest' && tag !== 'latest') {
          setImportTargetTag(tag);
        }
      }

      // If needs auth, show credentials form
      if (result.needs_auth) {
        setImportNeedsAuth(true);
      }
    } catch (error: unknown) {
      Logger.error('Erreur lors de la validation:', error);

      const apiError = error as { response?: { data?: { detail?: string } } };
      const detail = apiError.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t('imageValidationError'));
    } finally {
      setValidatingImage(false);
    }
  };

  const handleStartImport = async () => {
    if (!selectedProjectId) {
      toast.error(t('selectProjectBeforeImport'));
      return;
    }
    if (!importSourceImage.trim() || !importTargetName.trim()) {
      toast.error(t('sourceAndTargetRequired'));
      return;
    }

    try {
      setImportingImage(true);

      // Use stored credential_id if selected, otherwise inline credentials
      const importRequest: Parameters<typeof apiService.startImageImport>[0] = {
        source_image: importSourceImage,
        target_name: importTargetName,
        target_tag: importTargetTag || 'latest',
        project_id: selectedProjectId || undefined,
      };

      if (selectedCredentialId) {
        importRequest.credential_id = selectedCredentialId;
      } else if (importNeedsAuth && importUsername && importPassword) {
        importRequest.credentials = {
          registry_url: importRegistryUrl,
          username: importUsername,
          password: importPassword,
        };
      }

      const response = await apiService.startImageImport(importRequest);

      if (response.success && response.import_id) {
        setCurrentImportId(response.import_id);
        setShowImportProgress(true);
        toast.success(t('importStarted'));
      }
    } catch (error: unknown) {
      Logger.error('Erreur lors du demarrage de l\'import:', error);

      const apiError = error as { response?: { data?: { detail?: string | { message?: string } } } };
      const detail = apiError.response?.data?.detail;
      let errorMessage = t('importStartError');
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (typeof detail === 'object' && detail?.message) {
        errorMessage = detail.message;
      }
      toast.error(errorMessage);
    } finally {
      setImportingImage(false);
    }
  };

  const resetImportForm = () => {
    setImportSourceImage('');
    setImportTargetName('');
    setImportTargetTag('latest');
    setImageValidation(null);
    setImportNeedsAuth(false);
    setImportUsername('');
    setImportPassword('');
    setImportRegistryUrl('');
    setSelectedCredentialId(null);
    setCredentialValidation(null);
  };

  // ============================================================================
  // CREDENTIAL MANAGEMENT HANDLERS
  // ============================================================================

  const loadStoredCredentials = async () => {
    try {
      setLoadingCredentials(true);
      const response = await apiService.listCredentials(selectedProjectId || undefined);
      setStoredCredentials(response.credentials || []);
    } catch (error) {
      Logger.error('Erreur lors du chargement des credentials:', error);
      setStoredCredentials([]);
    } finally {
      setLoadingCredentials(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'import') {
      loadStoredCredentials();
    }
    if (activeTab === 'history') {
      loadBuildHistory();
    } else {
      setSelectedBuildIds(new Set());
    }
  }, [activeTab, selectedProjectId]);

  // Refresh real-time status for active builds by calling getBuildStatus (K8s live status)
  // getUserBuilds only returns DB status which is stale until getBuildStatus is called
  const refreshActiveBuildsStatus = async () => {
    const activeBuilds = buildHistory.filter(
      (b) => b.status === 'running' || b.status === 'pending'
    );
    if (activeBuilds.length === 0) return;

    let anyFinished = false;
    const updated = [...buildHistory];

    await Promise.all(
      activeBuilds.map(async (build) => {
        try {
          const liveStatus = await apiService.getBuildStatus(build.buildId);
          const idx = updated.findIndex((b) => b.buildId === build.buildId);
          if (idx !== -1 && updated[idx].status !== liveStatus.status) {
            updated[idx] = { ...updated[idx], status: liveStatus.status as BuildHistoryItem['status'] };
            if (['success', 'succeeded', 'failed', 'cancelled'].includes(liveStatus.status)) {
              anyFinished = true;
            }
          }
        } catch {
          // silently ignore per-build errors
        }
      })
    );

    // If any build just finished, reload full history to get duration/timestamps
    if (anyFinished) {
      await loadBuildHistory();
    } else if (updated.some((b, i) => b.status !== buildHistory[i]?.status)) {
      setBuildHistory(updated);
    }
  };

  // Auto-refresh polling for build history
  const refreshActiveRef = useRef(refreshActiveBuildsStatus);
  const loadBuildHistoryRef = useRef(loadBuildHistory);
  useEffect(() => {
    refreshActiveRef.current = refreshActiveBuildsStatus;
    loadBuildHistoryRef.current = loadBuildHistory;
  });

  useEffect(() => {
    const hasActiveBuilds = buildHistory.some(
      (build) => build.status === 'running' || build.status === 'pending'
    );

    // Always poll for new builds (webhook-triggered) every 10s
    const newBuildsInterval = setInterval(() => {
      loadBuildHistoryRef.current();
    }, 10_000);

    // When builds are active, also check live K8s status every 5s
    let statusInterval: ReturnType<typeof setInterval> | null = null;
    if (hasActiveBuilds) {
      statusInterval = setInterval(() => {
        refreshActiveRef.current();
      }, 5_000);
    }

    return () => {
      clearInterval(newBuildsInterval);
      if (statusInterval) clearInterval(statusInterval);
    };
  }, [buildHistory, selectedProjectId]);

  const handleVerifyAndStoreCredentials = async () => {
    if (!importRegistryUrl.trim() || !importUsername.trim() || !importPassword.trim()) {
      toast.error(t('credentialsRequired'));
      return;
    }

    try {
      setVerifyingCredentials(true);
      setCredentialValidation(null);

      const result = await apiService.storeCredentials({
        registry_url: importRegistryUrl,
        username: importUsername,
        password: importPassword,
        projet_id: selectedProjectId || undefined,
      });

      setCredentialValidation({
        valid: result.valid,
        message: result.message,
        credential_id: result.credential_id,
      });

      if (result.valid && result.credential_id) {
        toast.success(t('credentialsValid'));
        setSelectedCredentialId(result.credential_id);
        setImportPassword('');
        await loadStoredCredentials();
      } else {
        toast.error(result.message || t('credentialsInvalid'));
      }
    } catch (error: unknown) {
      Logger.error('Erreur lors de la verification:', error);
      const apiError = error as { response?: { data?: { detail?: string } } };
      const detail = apiError.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 'Erreur lors de la verification';
      setCredentialValidation({ valid: false, message });
      toast.error(message);
    } finally {
      setVerifyingCredentials(false);
    }
  };

  const handleSelectCredential = (credential: RegistryCredentialItem) => {
    setSelectedCredentialId(credential.id);
    setImportRegistryUrl(credential.registry_url);
    setImportUsername(credential.username);
    setImportPassword('');
    setImportNeedsAuth(true);
    setCredentialValidation(null);
  };

  const handleDeleteCredential = async (credentialId: string) => {
    try {
      setDeletingCredentialId(credentialId);
      await apiService.deleteCredential(credentialId);
      toast.success(t('credentialDeleted'));
      if (selectedCredentialId === credentialId) {
        setSelectedCredentialId(null);
      }
      await loadStoredCredentials();
    } catch (error) {
      Logger.error('Erreur lors de la suppression:', error);
      toast.error(t('credentialDeleteError'));
    } finally {
      setDeletingCredentialId(null);
    }
  };

  const handleRevalidateCredential = async (credentialId: string) => {
    try {
      setRevalidatingId(credentialId);
      const result = await apiService.revalidateCredential(credentialId);
      if (result.valid) {
        toast.success(t('credentialValid'));
      } else {
        toast.error(result.message || t('credentialInvalidMsg'));
      }
      await loadStoredCredentials();
    } catch (error) {
      Logger.error('Erreur lors de la re-validation:', error);
      toast.error(t('credentialRevalidateError'));
    } finally {
      setRevalidatingId(null);
    }
  };

  const getProviderIcon = (provider: string): React.ReactNode => {
    switch (provider.toLowerCase()) {
      case 'github':
        return <RIcon icon={FaGithub} className="w-5 h-5" />;
      case 'gitlab':
        return <RIcon icon={FaGitlab} className="w-5 h-5" />;
      default:
        return <ArchiveBoxIcon className="w-5 h-5" />;
    }
  };

  const getProviderColor = (provider: string): string => {
    switch (provider.toLowerCase()) {
      case 'github':
        return 'bg-gray-800';
      case 'gitlab':
        return 'bg-orange-600';
      default:
        return 'bg-gray-600';
    }
  };

  const handleOpenBuild = (buildId: string) => {
    if (buildId.startsWith('import-')) {
      setCurrentImportId(buildId);
      setShowImportProgress(true);
    } else {
      setCurrentBuildId(buildId);
      setShowBuildProgress(true);
    }
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      running: 'bg-blue-500/12 text-blue-400',
      pending: 'bg-surface-3 text-text-secondary',
      success: 'bg-green-500/12 text-green-400',
      succeeded: 'bg-green-500/12 text-green-400',
      failed: 'bg-red-500/12 text-red-400',
      cancelled: 'bg-yellow-500/12 text-yellow-400'
    };
    const icons: Record<string, React.ReactNode> = {
      running: <ArrowPathIcon className="w-4 h-4" />,
      pending: <ClockIcon className="w-4 h-4" />,
      success: <CheckCircleIcon className="w-4 h-4" />,
      succeeded: <CheckCircleIcon className="w-4 h-4" />,
      failed: <XCircleIcon className="w-4 h-4" />,
      cancelled: <StopCircleIcon className="w-4 h-4" />,
    };
    const labelKeys: Record<string, string> = {
      running: 'status.running',
      pending: 'status.pending',
      success: 'status.success',
      succeeded: 'status.succeeded',
      failed: 'status.failed',
      cancelled: 'status.cancelled'
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colors[status as keyof typeof colors] || colors.pending}`}>
        <span className="mr-1">{icons[status] || icons.pending}</span>
        {t(labelKeys[status] || 'status.unknown')}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" role="status" aria-live="polite" aria-busy="true">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500" aria-hidden="true"></div>
        <span className="sr-only">{t('loadingBuilds')}</span>
      </div>
    );
  }

  return (
    <>
        {/* Header */}
        <div className="md:flex md:items-center md:justify-between mb-8">
          <div className="flex-1 min-w-0">
            <h1 className="text-3xl font-bold text-text-primary">
              {t('title')}
            </h1>
            <p className="mt-2 text-sm text-text-tertiary">
              {t('subtitle')}
            </p>
          </div>
          <div className="mt-4 flex md:mt-0 md:ml-4">
            <button
              onClick={() => {
                if (!selectedProjectId) {
                  toast.error(t('selectProjectFirst'));
                  return;
                }
                setShowConnectRepoModal(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-card text-sm font-medium text-white bg-accent-600 hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('connectRepo')}
            </button>
          </div>
        </div>

        {/* Project Scope Selector */}
        <div className="mb-6 flex items-center gap-3">
          <label className="text-sm font-medium text-text-secondary">
            {t('scope.label')}
          </label>
          {projects.length > 0 ? (
            <>
              <select
                value={selectedProjectId || ''}
                onChange={(e) => setSelectedProjectId(e.target.value || null)}
                className={`block w-64 border rounded-md shadow-card py-2 px-3 bg-surface-3 text-text-primary text-sm focus:outline-none focus:ring-accent-500 focus:border-accent-500 ${
                  !selectedProjectId
                    ? 'border-amber-400'
                    : 'border-border-1'
                }`}
              >
                <option value="">{t('scope.selectProject')}</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nom}
                  </option>
                ))}
              </select>
              {!selectedProjectId && (
                <span className="text-xs text-amber-400">
                  {t('scope.noProjectWarning')}
                </span>
              )}
              {selectedProjectId && (
                <span className="text-xs text-text-tertiary">
                  {t('scope.showsAllMembers')}
                </span>
              )}
            </>
          ) : (
            <span className="text-sm text-amber-400">
              {t('scope.noProjectAvailable')}
            </span>
          )}
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-border-1 overflow-x-auto scrollbar-hide">
          <nav className="-mb-px flex space-x-4 min-w-max">
            <button
              onClick={() => setActiveTab('repos')}
              className={`${
                activeTab === 'repos'
                  ? 'border-accent-500 text-accent-400'
                  : 'border-transparent text-text-tertiary hover:text-text-primary hover:border-border-2'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
            >
              <ArchiveBoxIcon className="w-4 h-4 inline mr-1" /> {t('tabs.repos')}
            </button>
            <button
              onClick={() => setActiveTab('public')}
              className={`${
                activeTab === 'public'
                  ? 'border-accent-500 text-accent-400'
                  : 'border-transparent text-text-tertiary hover:text-text-primary hover:border-border-2'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
            >
              <GlobeAltIcon className="w-4 h-4 inline mr-1" /> {t('tabs.public')}
            </button>
            <button
              onClick={() => setActiveTab('import')}
              className={`${
                activeTab === 'import'
                  ? 'border-accent-500 text-accent-400'
                  : 'border-transparent text-text-tertiary hover:text-text-primary hover:border-border-2'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
            >
              <ArrowDownTrayIcon className="w-4 h-4 inline mr-1" /> {t('tabs.import')}
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`${
                activeTab === 'history'
                  ? 'border-accent-500 text-accent-400'
                  : 'border-transparent text-text-tertiary hover:text-text-primary hover:border-border-2'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center`}
            >
              <ChartBarIcon className="w-4 h-4 inline mr-1" /> {t('tabs.history')}
              {buildHistory.length > 0 && (
                <span className="ml-2 bg-blue-500/12 text-blue-400 py-0.5 px-2 rounded-full text-xs">
                  {buildHistory.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('registry')}
              className={`${
                activeTab === 'registry'
                  ? 'border-accent-500 text-accent-400'
                  : 'border-transparent text-text-tertiary hover:text-text-primary hover:border-border-2'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
            >
              <ArchiveBoxIcon className="w-4 h-4 inline mr-1" /> {t('tabs.registry')}
            </button>
          </nav>
        </div>

        {/* Quick Links - Only show on repos tab */}
        {activeTab === 'repos' && (
          <div className="mb-8 bg-blue-500/12 border border-blue-500/20 rounded-lg p-4">
            <div className="flex items-start">
              <InformationCircleIcon className="w-6 h-6 text-blue-500 mr-3 flex-shrink-0" />
              <div className="flex-1 text-sm text-blue-400">
                <p className="font-medium mb-1">{t('howItWorks.title')}</p>
                <ol className="list-decimal list-inside space-y-1 text-xs">
                  <li>{t('howItWorks.step1')}</li>
                  <li>{t('howItWorks.step2')}</li>
                  <li>{t('howItWorks.step3')} <Link to="/deploy" className="underline font-medium">{t('howItWorks.step3Link')}</Link></li>
                  <li>{t('howItWorks.step4')} <Link to="/built-images" className="underline font-medium">{t('howItWorks.step4Link')}</Link></li>
                </ol>
              </div>
            </div>
          </div>
        )}

        {/* Connected Repositories Tab */}
        {activeTab === 'repos' && connectedRepos.length === 0 ? (
          <div className="text-center py-12 bg-surface-2 rounded-lg border border-border-1">
            <svg className="mx-auto h-12 w-12 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-text-primary">{t('emptyRepos.title')}</h3>
            <p className="mt-1 text-sm text-text-tertiary">
              {t('emptyRepos.subtitle')}
            </p>
            <div className="mt-6">
              <button
                onClick={() => {
                  if (!selectedProjectId) {
                    toast.error(t('selectProjectFirst'));
                    return;
                  }
                  setShowConnectRepoModal(true);
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-card text-sm font-medium rounded-md text-white bg-accent-600 hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {t('connectRepo')}
              </button>
            </div>
          </div>
        ) : activeTab === 'repos' ? (
          <div className="space-y-3">
            {connectedRepos.map((repo) => {
              const providerName = repo.oauth_provider.name.toLowerCase();
              const borderColor = providerName === 'github' ? 'border-l-gray-400' : providerName === 'gitlab' ? 'border-l-orange-500' : 'border-l-gray-400';

              return (
                <div
                  key={repo.id}
                  className={`bg-surface-2 rounded-lg border border-border-1 border-l-4 ${borderColor} shadow-card hover:shadow-md transition-shadow`}
                >
                  <div className="px-5 py-4 flex items-center justify-between">
                    {/* Left: provider icon + repo info */}
                    <div className="flex items-center min-w-0 flex-1 gap-4">
                      <div className={`flex-shrink-0 w-10 h-10 ${getProviderColor(repo.oauth_provider.name)} rounded-lg flex items-center justify-center text-white`}>
                        {getProviderIcon(repo.oauth_provider.name)}
                      </div>

                      <div className="min-w-0 flex-1">
                        <h3 className="text-base font-bold text-text-primary truncate">
                          {repo.repo_name}
                        </h3>
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-surface-3 text-text-secondary font-mono">
                            {repo.monitored_branch || repo.default_branch || 'main'}
                          </span>
                          {repo.clone_url && (
                            <a
                              href={repo.clone_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-accent-400 hover:underline text-xs truncate max-w-xs hidden sm:inline"
                            >
                              {repo.clone_url.replace(/^https?:\/\//, '')}
                            </a>
                          )}
                          <span className="text-text-tertiary text-xs hidden md:inline">
                            {t('connectedOn', { date: new Date(repo.created_at).toLocaleDateString() })}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Right: action buttons */}
                    <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                      <button
                        onClick={() => handleOpenBuildConfig(repo)}
                        aria-label={`${t('configureBuilds')} ${repo.repo_name}`}
                        className="p-2 text-text-tertiary hover:text-text-primary hover:bg-surface-3 rounded-lg transition-colors"
                        title={t('configureBuilds') as string}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeleteRepo(repo.id)}
                        aria-label={`${t('history.delete')} ${repo.repo_name}`}
                        className="p-2 text-text-tertiary hover:text-red-400 hover:bg-red-500/12 rounded-lg transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleStartBuild(repo)}
                        disabled={buildingRepoId === repo.id || !selectedProjectId}
                        title={!selectedProjectId ? t('selectProjectBeforeBuild') as string : undefined}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {buildingRepoId === repo.id ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            {t('starting')}
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Build
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}

        {/* Public Repository Tab */}
        {activeTab === 'public' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-surface-2 rounded-lg border border-border-1 p-6">
              <div className="mb-6">
                <h3 className="text-lg font-bold text-text-primary mb-2">
                  <GlobeAltIcon className="w-4 h-4 inline mr-1" /> {t('public.title')}
                </h3>
                <p className="text-sm text-text-tertiary">
                  {t('public.subtitle')}
                </p>
              </div>

              <div className="space-y-4">
                {/* Repository URL */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    {t('public.repoUrl')}
                  </label>
                  <input
                    type="url"
                    value={publicRepoUrl}
                    onChange={(e) => setPublicRepoUrl(e.target.value)}
                    placeholder="https://github.com/username/repo.git"
                    className="w-full px-4 py-2 border border-border-1 rounded-md bg-surface-3 text-text-primary placeholder-text-quaternary focus:outline-none focus:ring-2 focus:ring-accent-500"
                  />
                  <p className="mt-1 text-xs text-text-tertiary">
                    {t('public.repoUrlHelp')}
                  </p>
                </div>

                {/* Branch */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    {t('public.branch')}
                  </label>
                  <input
                    type="text"
                    value={publicRepoBranch}
                    onChange={(e) => setPublicRepoBranch(e.target.value)}
                    placeholder="main"
                    className="w-full px-4 py-2 border border-border-1 rounded-md bg-surface-3 text-text-primary placeholder-text-quaternary focus:outline-none focus:ring-2 focus:ring-accent-500"
                  />
                </div>

                {/* Image Tag */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    {t('public.imageTag')}
                  </label>
                  <input
                    type="text"
                    value={publicImageTag}
                    onChange={(e) => setPublicImageTag(e.target.value)}
                    placeholder="latest, v1.0.0, staging..."
                    className="w-full px-4 py-2 border border-border-1 rounded-md bg-surface-3 text-text-primary placeholder-text-quaternary focus:outline-none focus:ring-2 focus:ring-accent-500"
                  />
                  <p className="mt-1 text-xs text-text-tertiary">
                    {t('public.imageTagHelp')}
                  </p>
                </div>

                {/* Source Subpath for Monorepo */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    {t('public.sourceSubpath')}
                  </label>
                  <input
                    type="text"
                    value={publicSourceSubpath}
                    onChange={(e) => setPublicSourceSubpath(e.target.value)}
                    placeholder="ex: apps/backend, packages/api"
                    className="w-full px-4 py-2 border border-border-1 rounded-md bg-surface-3 text-text-primary placeholder-text-quaternary focus:outline-none focus:ring-2 focus:ring-accent-500"
                  />
                  <p className="mt-1 text-xs text-text-tertiary">
                    {t('public.sourceSubpathHelp')}
                  </p>
                </div>

                {/* Info Box */}
                <div className="bg-blue-500/12 border border-blue-500/20 rounded-lg p-4">
                  <div className="flex items-start">
                    <InformationCircleIcon className="w-6 h-6 text-blue-500 mr-3 flex-shrink-0" />
                    <div className="flex-1 text-sm text-blue-400">
                      <p className="font-medium mb-1">{t('public.importantInfo')}</p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>{t('public.info1')}</li>
                        <li>{t('public.info2')}</li>
                        <li>{t('public.info3')}</li>
                        <li>{t('public.info4')}</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Project required warning */}
                {!selectedProjectId && (
                  <div className="bg-amber-500/8 border border-amber-500/20 rounded-lg p-3 flex items-center gap-2">
                    <ExclamationTriangleIcon className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    <p className="text-sm text-amber-400">{t('public.selectProjectWarning')}</p>
                  </div>
                )}

                {/* Build Button */}
                <button
                  onClick={handleBuildPublicRepo}
                  disabled={buildingPublicRepo || !publicRepoUrl.trim() || !selectedProjectId}
                  title={!selectedProjectId ? t('selectProjectBeforeBuild') as string : undefined}
                  className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {buildingPublicRepo ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {t('public.startingBuild')}
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {t('public.launchBuild')}
                    </>
                  )}
                </button>
              </div>

              {/* Examples */}
              <div className="mt-6 pt-6 border-t border-border-1">
                <p className="text-sm font-medium text-text-secondary mb-3">
                  {t('public.validUrls')}
                </p>
                <div className="space-y-2 text-xs font-mono">
                  <div className="p-2 bg-surface-3/50 rounded text-text-secondary">
                    https://github.com/username/repo.git
                  </div>
                  <div className="p-2 bg-surface-3/50 rounded text-text-secondary">
                    https://gitlab.com/username/project
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Import Image Tab */}
        {activeTab === 'import' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-surface-2 rounded-lg border border-border-1 p-6">
              <div className="mb-6">
                <h3 className="text-lg font-bold text-text-primary mb-2">
                  <ArrowDownTrayIcon className="w-4 h-4 inline mr-1" /> {t('tabs.import')} Docker
                </h3>
                <p className="text-sm text-text-tertiary">
                  {t('import.subtitle')}
                </p>
              </div>

              <div className="space-y-4">
                {/* Source Image */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    {t('import.sourceImage')}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={importSourceImage}
                      onChange={(e) => {
                        setImportSourceImage(e.target.value);
                        setImageValidation(null);
                      }}
                      placeholder="nginx:1.25, ghcr.io/user/app:v2, registry.gitlab.com/user/app:latest"
                      className="flex-1 px-4 py-2 border border-border-1 rounded-md bg-surface-3 text-text-primary placeholder-text-quaternary focus:outline-none focus:ring-2 focus:ring-accent-500"
                    />
                    <button
                      onClick={handleValidateImage}
                      disabled={validatingImage || !importSourceImage.trim()}
                      className="px-4 py-2 bg-accent-600 text-white rounded-md hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                    >
                      {validatingImage ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          {t('import.verifying')}
                        </span>
                      ) : (
                        t('import.verify')
                      )}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-text-tertiary">
                    {t('import.sourceImageHelp')}
                  </p>
                </div>

                {/* Validation Result */}
                {imageValidation && (
                  <div className={`p-4 rounded-lg border ${
                    imageValidation.exists
                      ? 'bg-green-500/12 border-green-500/20'
                      : 'bg-red-500/12 border-red-500/20'
                  }`}>
                    <div className="flex items-start">
                      <span className="mr-3 flex-shrink-0">{imageValidation.exists ? <CheckCircleIcon className="w-5 h-5 text-green-500" /> : <XCircleIcon className="w-5 h-5 text-red-500" />}</span>
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${
                          imageValidation.exists
                            ? 'text-green-400'
                            : 'text-red-400'
                        }`}>
                          {imageValidation.message}
                        </p>
                        {imageValidation.exists && (
                          <div className="mt-2 text-xs space-y-1">
                            <p className="text-green-400">
                              Registry: <span className="font-mono">{imageValidation.registry}</span>
                            </p>
                            <p className="text-green-400">
                              Repository: <span className="font-mono">{imageValidation.repository}</span>
                            </p>
                            <p className="text-green-400">
                              Tag: <span className="font-mono">{imageValidation.tag}</span>
                            </p>
                            {imageValidation.size_bytes && (
                              <p className="text-green-400">
                                    {t('import.size')} <span className="font-mono">
                                  {imageValidation.size_bytes < 1024 * 1024
                                    ? `${(imageValidation.size_bytes / 1024).toFixed(1)} KB`
                                    : imageValidation.size_bytes < 1024 * 1024 * 1024
                                    ? `${(imageValidation.size_bytes / (1024 * 1024)).toFixed(1)} MB`
                                    : `${(imageValidation.size_bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
                                  }
                                </span>
                              </p>
                            )}
                          </div>
                        )}
                        {imageValidation.needs_auth && (
                          <p className="mt-2 text-xs text-amber-400 font-medium">
                            <ExclamationTriangleIcon className="w-4 h-4 inline mr-1" /> {t('import.needsAuth')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Stored Credentials Section - "Mes Registries" */}
                <div className="border border-border-1 rounded-lg overflow-hidden">
                  <div className="bg-surface-3/50 px-4 py-3 border-b border-border-1">
                    <h4 className="text-sm font-medium text-text-primary flex items-center">
                      <svg className="w-4 h-4 mr-2 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                      </svg>
                      {t('import.myRegistries')}
                    </h4>
                  </div>
                  <div className="p-4">
                    {loadingCredentials ? (
                      <div className="flex items-center justify-center py-4 text-text-tertiary">
                        <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span className="text-sm">{t('import.loading')}</span>
                      </div>
                    ) : storedCredentials.length === 0 ? (
                      <p className="text-sm text-text-tertiary text-center py-3">
                        {t('import.noCredentials')}
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {storedCredentials.map((cred) => (
                          <div
                            key={cred.id}
                            className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                              selectedCredentialId === cred.id
                                ? 'border-accent-500 bg-accent-500/12'
                                : 'border-border-1 bg-surface-2'
                            }`}
                          >
                            <div className="flex items-center min-w-0 flex-1">
                              <span className="text-lg mr-2 flex-shrink-0">
                                {cred.registry_url?.includes('ghcr.io') ? <RIcon icon={FaGithub} className="w-5 h-5" /> :
                                 cred.registry_url?.includes('gitlab') ? <RIcon icon={FaGitlab} className="w-5 h-5" /> :
                                 cred.registry_url?.includes('docker') ? <RIcon icon={FaDocker} className="w-5 h-5" /> : <ArchiveBoxIcon className="w-5 h-5" />}
                              </span>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-text-primary truncate">
                                  {cred.registry_url}
                                </p>
                                <p className="text-xs text-text-tertiary truncate">
                                  {cred.username}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                                {/* Status badge */}
                                {cred.is_valid === true && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/12 text-green-400">
                                    {t('import.active')}
                                  </span>
                                )}
                                {cred.is_valid === false && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/12 text-red-400" title={cred.validation_error || ''}>
                                    {t('import.invalid')}
                                  </span>
                                )}
                                {cred.is_valid === undefined || cred.is_valid === null ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-surface-3 text-text-secondary">
                                    {t('import.notVerified')}
                                  </span>
                                ) : null}
                                {/* Selected badge */}
                                {selectedCredentialId === cred.id && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/12 text-blue-400">
                                    {t('import.selected')}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 ml-3 flex-shrink-0">
                              {selectedCredentialId !== cred.id && (
                                <button
                                  onClick={() => handleSelectCredential(cred)}
                                  className="px-2 py-1 text-xs font-medium text-blue-400 hover:bg-blue-500/12 rounded transition-colors"
                                >
                                  {t('import.use')}
                                </button>
                              )}
                              <button
                                onClick={() => handleRevalidateCredential(cred.id)}
                                disabled={revalidatingId === cred.id}
                                className="px-2 py-1 text-xs font-medium text-text-secondary hover:bg-surface-3 rounded transition-colors disabled:opacity-50"
                                title={t('import.reVerify') as string}
                              >
                                {revalidatingId === cred.id ? (
                                  <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                  </svg>
                                ) : (
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                )}
                              </button>
                              <button
                                onClick={() => handleDeleteCredential(cred.id)}
                                disabled={deletingCredentialId === cred.id}
                                className="px-2 py-1 text-xs font-medium text-red-400 hover:bg-red-500/12 rounded transition-colors disabled:opacity-50"
                                title={t('history.delete') as string}
                              >
                                {deletingCredentialId === cred.id ? (
                                  <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                  </svg>
                                ) : (
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                )}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Credentials Form - Add/Modify */}
                <div className="p-4 bg-amber-500/8 border border-amber-500/20 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-amber-400 flex items-center">
                      <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      {importNeedsAuth ? t('import.authCredentials') : t('import.addCredentials')}
                    </h4>
                    {!importNeedsAuth && (
                      <button
                        onClick={() => setImportNeedsAuth(true)}
                        className="text-xs font-medium text-accent-400 hover:text-accent-300"
                      >
                        {t('import.configure')}
                      </button>
                    )}
                  </div>
                  {importNeedsAuth && (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">
                          {t('import.registryUrl')}
                        </label>
                        <input
                          type="text"
                          value={importRegistryUrl}
                          onChange={(e) => { setImportRegistryUrl(e.target.value); setCredentialValidation(null); }}
                          placeholder="ghcr.io, registry.gitlab.com, docker.io"
                          className="w-full px-3 py-2 border border-border-1 rounded-md bg-surface-3 text-text-primary placeholder-text-quaternary focus:outline-none focus:ring-2 focus:ring-accent-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">
                          {t('import.username')}
                        </label>
                        <input
                          type="text"
                          value={importUsername}
                          onChange={(e) => { setImportUsername(e.target.value); setCredentialValidation(null); }}
                          placeholder="username"
                          className="w-full px-3 py-2 border border-border-1 rounded-md bg-surface-3 text-text-primary placeholder-text-quaternary focus:outline-none focus:ring-2 focus:ring-accent-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-text-secondary mb-1">
                          {t('import.password')}
                        </label>
                        <input
                          type="password"
                          value={importPassword}
                          onChange={(e) => { setImportPassword(e.target.value); setCredentialValidation(null); }}
                          placeholder="password ou token d'acces"
                          className="w-full px-3 py-2 border border-border-1 rounded-md bg-surface-3 text-text-primary placeholder-text-quaternary focus:outline-none focus:ring-2 focus:ring-accent-500 text-sm"
                        />
                      </div>

                      {/* Validation result badge */}
                      {credentialValidation && (
                        <div className={`flex items-center gap-2 p-2 rounded-md text-xs ${
                          credentialValidation.valid
                            ? 'bg-green-500/12 text-green-400'
                            : 'bg-red-500/12 text-red-400'
                        }`}>
                          <span>{credentialValidation.valid ? <CheckCircleIcon className="w-5 h-5 text-green-500" /> : <XCircleIcon className="w-5 h-5 text-red-500" />}</span>
                          <span>{credentialValidation.message}</span>
                        </div>
                      )}

                      <div className="flex items-center gap-2 pt-1">
                        <button
                          onClick={handleVerifyAndStoreCredentials}
                          disabled={verifyingCredentials || !importRegistryUrl.trim() || !importUsername.trim() || !importPassword.trim()}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-accent-600 hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {verifyingCredentials ? (
                            <>
                              <svg className="animate-spin -ml-0.5 mr-1.5 h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              {t('import.verifying')}
                            </>
                          ) : (
                            <>
                              <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {t('import.verifyAndStore')}
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setImportNeedsAuth(false);
                            setImportUsername('');
                            setImportPassword('');
                            setImportRegistryUrl('');
                            setSelectedCredentialId(null);
                            setCredentialValidation(null);
                          }}
                          className="text-xs text-text-tertiary hover:text-text-primary"
                        >
                          {t('import.removeAuth')}
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* Target Name */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    {t('import.targetName')}
                  </label>
                  <input
                    type="text"
                    value={importTargetName}
                    onChange={(e) => setImportTargetName(e.target.value)}
                    placeholder="nom-de-l-image (auto-genere depuis l'image source)"
                    className="w-full px-4 py-2 border border-border-1 rounded-md bg-surface-3 text-text-primary placeholder-text-quaternary focus:outline-none focus:ring-2 focus:ring-accent-500"
                  />
                  <p className="mt-1 text-xs text-text-tertiary">
                    {t('import.targetNameHelp')}
                  </p>
                </div>

                {/* Target Tag */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    {t('import.targetTag')}
                  </label>
                  <input
                    type="text"
                    value={importTargetTag}
                    onChange={(e) => setImportTargetTag(e.target.value)}
                    placeholder="latest"
                    className="w-full px-4 py-2 border border-border-1 rounded-md bg-surface-3 text-text-primary placeholder-text-quaternary focus:outline-none focus:ring-2 focus:ring-accent-500"
                  />
                </div>

                {/* Info Box */}
                <div className="bg-blue-500/12 border border-blue-500/20 rounded-lg p-4">
                  <div className="flex items-start">
                    <InformationCircleIcon className="w-6 h-6 text-blue-500 mr-3 flex-shrink-0" />
                    <div className="flex-1 text-sm text-blue-400">
                      <p className="font-medium mb-1">{t('import.howItWorks')}</p>
                      <ol className="list-decimal list-inside space-y-1 text-xs">
                        <li>{t('import.step1')} <span className="font-mono">nginx:1.25-alpine</span>)</li>
                        <li>{t('import.step2')}</li>
                        <li>{t('import.step3')}</li>
                        <li>{t('import.step4')}</li>
                        <li>{t('import.step5')} <Link to="/built-images" className="underline font-medium">{t('import.step5Link')}</Link></li>
                      </ol>
                    </div>
                  </div>
                </div>

                {/* Import Button */}
                <button
                  onClick={handleStartImport}
                  disabled={importingImage || !importSourceImage.trim() || !importTargetName.trim()}
                  className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {importingImage ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {t('import.startingImport')}
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      {t('import.importImage')}
                    </>
                  )}
                </button>
              </div>

              {/* Registry Examples */}
              <div className="mt-6 pt-6 border-t border-border-1">
                <p className="text-sm font-medium text-text-secondary mb-3">
                  {t('import.validImages')}
                </p>
                <div className="space-y-2 text-xs font-mono">
                  <div className="p-2 bg-surface-3/50 rounded text-text-secondary flex items-center">
                    <RIcon icon={FaDocker} className="w-4 h-4 mr-2 inline" /> nginx:1.25-alpine
                  </div>
                  <div className="p-2 bg-surface-3/50 rounded text-text-secondary flex items-center">
                    <RIcon icon={FaGithub} className="w-4 h-4 mr-2 inline" /> ghcr.io/username/my-app:v2.0
                  </div>
                  <div className="p-2 bg-surface-3/50 rounded text-text-secondary flex items-center">
                    <RIcon icon={FaGitlab} className="w-4 h-4 mr-2 inline" /> registry.gitlab.com/group/project:latest
                  </div>
                  <div className="p-2 bg-surface-3/50 rounded text-text-secondary flex items-center">
                    <RIcon icon={FaDocker} className="w-4 h-4 mr-2 inline" /> python:3.12-slim
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Build History Tab */}
        {activeTab === 'history' && (
          <div className="bg-surface-2 rounded-lg border border-border-1 overflow-hidden">
            {buildHistory.length === 0 ? (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-text-primary">{t('history.emptyTitle')}</h3>
                <p className="mt-1 text-sm text-text-tertiary">
                  {t('history.emptySubtitle')}
                </p>
                <div className="mt-6">
                  <button
                    onClick={() => setActiveTab('repos')}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-card text-sm font-medium rounded-md text-white bg-accent-600 hover:bg-accent-700"
                  >
                    {t('history.viewRepos')}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {selectedBuildIds.size > 0 && (
                  <div className="flex items-center justify-between px-6 py-3 bg-blue-500/12 border-b border-border-1">
                    <span className="text-sm text-blue-400">
                      {t('history.selected', { count: selectedBuildIds.size })}
                    </span>
                    <button
                      onClick={deleteSelectedBuilds}
                      disabled={deletingSelected}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-md"
                    >
                      {deletingSelected ? (
                        <>
                          <ArrowPathIcon className="w-4 h-4 mr-1.5 animate-spin" />
                          {t('history.deleting')}
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          {t('history.deleteSelection')}
                        </>
                      )}
                    </button>
                  </div>
                )}
                <div className="overflow-x-auto">
                <table className="w-full divide-y divide-border-1 table-fixed">
                  <thead className="bg-surface-3">
                    <tr>
                      <th className="px-2 py-3 w-10">
                        <input
                          type="checkbox"
                          checked={selectableBuilds.length > 0 && selectedBuildIds.size === selectableBuilds.length}
                          onChange={toggleSelectAll}
                          disabled={selectableBuilds.length === 0}
                          className="h-4 w-4 text-accent-600 border-border-1 rounded focus:ring-accent-500"
                        />
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                        {t('history.repository')}
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider w-20">
                        {t('history.branch')}
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                        {t('history.started')}
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider w-16">
                        {t('history.duration')}
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider w-24">
                        {t('history.status')}
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-text-tertiary uppercase tracking-wider w-28">
                        {t('history.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-surface-2 divide-y divide-border-1">
                    {buildHistory.map((build) => (
                      <tr key={build.buildId} className={`hover:bg-surface-3 ${selectedBuildIds.has(build.buildId) ? 'bg-accent-500/8' : ''}`}>
                        <td className="px-2 py-3 w-10">
                          {terminalStatuses.includes(build.status) ? (
                            <input
                              type="checkbox"
                              checked={selectedBuildIds.has(build.buildId)}
                              onChange={() => toggleBuildSelection(build.buildId)}
                              className="h-4 w-4 text-accent-600 border-border-1 rounded focus:ring-accent-500"
                            />
                          ) : (
                            <span className="block w-4" />
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center min-w-0">
                            <span className="text-lg mr-2 shrink-0">{getProviderIcon(build.provider)}</span>
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-text-primary truncate">
                                {build.repoName}
                              </div>
                              <div className="text-xs text-text-tertiary truncate">
                                {build.repoOwner}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-surface-3 text-xs font-mono text-text-secondary truncate max-w-full">
                            {build.branch}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-sm text-text-tertiary whitespace-nowrap">
                          {new Date(build.startedAt).toLocaleString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td className="px-3 py-3 text-sm text-text-tertiary whitespace-nowrap">
                          {build.duration != null ? (
                            build.duration >= 60
                              ? `${Math.floor(build.duration / 60)}m ${build.duration % 60}s`
                              : `${build.duration}s`
                          ) : (
                            ['running', 'pending'].includes(build.status)
                              ? <ArrowPathIcon className="w-4 h-4 animate-spin text-blue-400" />
                              : '—'
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex flex-col items-start gap-1">
                            {getStatusBadge(build.status)}
                            {/* Un build « reussi » dont la mise en service a echoue affichait
                                exactement la meme chose qu'un build livre : le pastille ci-dessous
                                est la seule chose qui les distingue a l'ecran. On ne rend RIEN
                                quand le verdict est absent -- c'est l'etat « jamais tentee ». */}
                            {build.livraisonAuto && (
                              <span
                                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${verdictClasse(build.livraisonAuto.etat)}`}
                                title={[t('verdict.titre') as string,
                                        build.livraisonAuto.message || '']
                                        .filter(Boolean).join(' — ')}
                              >
                                {verdictEstMauvais(build.livraisonAuto.etat) && (
                                  <ExclamationTriangleIcon className="w-3 h-3" />
                                )}
                                {t(`verdict.${build.livraisonAuto.etat}`) as string}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleOpenBuild(build.buildId)}
                              className="text-accent-400 hover:text-accent-300 p-1"
                              title={t('history.view') as string}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                            {['success', 'succeeded'].includes(build.status)
                              && build.connectedRepositoryId != null && (
                              <button
                                onClick={() => setBuildADeployer(build)}
                                className="text-green-400 hover:text-green-300 p-1"
                                title={t('deploy.title') as string}
                                aria-label={`${t('deploy.title')} ${build.buildId}`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                    d="M5 10l7-7m0 0l7 7m-7-7v18" />
                                </svg>
                              </button>
                            )}
                            {['success', 'succeeded', 'failed', 'cancelled'].includes(build.status) && (
                              <button
                                onClick={() => {
                                  if (window.confirm(t('deleteBuildConfirm') as string)) {
                                    deleteBuildFromHistory(build.buildId);
                                  }
                                }}
                                className="text-red-400 hover:text-red-300 p-1"
                                title={t('history.delete') as string}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </div>
            )}
          </div>
        )}

        {/* NubiRegistry Tab */}
        {activeTab === 'registry' && (
          <BuiltImagesPage />
        )}

      {/* Repository Connector Modal - Using new V2 hybrid system */}
      <RepositoryConnectorV2
        isOpen={showConnectRepoModal}
        onClose={() => setShowConnectRepoModal(false)}
        onConnected={() => {
          setShowConnectRepoModal(false);
          loadConnectedRepositories();
        }}
        projectId={selectedProjectId || undefined}
      />

      {/* Branch Selection Modal */}
      {showBranchSelectModal && selectedRepoForBuild && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-labelledby="branch-select-title">
          <div className="bg-surface-2 rounded-lg shadow-2xl max-w-md w-full mx-4">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-border-1">
              <h3 id="branch-select-title" className="text-lg font-bold text-text-primary">
                {t('branchModal.title')}
              </h3>
              <button
                onClick={() => {
                  setShowBranchSelectModal(false);
                  setSelectedRepoForBuild(null);
                }}
                aria-label={t('branchModal.close') as string}
                className="text-text-tertiary hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-500 rounded"
              >
                <span aria-hidden="true">&#x2715;</span>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Repository Info */}
              <div className="bg-surface-3 p-4 rounded-lg">
                <p className="text-sm text-text-tertiary mb-1">Repository</p>
                <p className="font-semibold text-text-primary">
                  {selectedRepoForBuild.repo_owner}/{selectedRepoForBuild.repo_name}
                </p>
              </div>

              {/* Branch Input */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  {t('branchModal.branchToBuild')}
                </label>

                {loadingBranches && (
                  <div className="flex items-center justify-center py-3 text-text-tertiary">
                    <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('branchModal.loadingBranches')}
                  </div>
                )}

                {!loadingBranches && availableBranches.length > 0 && (
                  <select
                    value={buildBranch}
                    onChange={(e) => setBuildBranch(e.target.value)}
                    className="w-full px-4 py-2 border border-border-1 rounded-lg
                             bg-surface-3 text-text-primary
                             focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent
                             transition-all"
                  >
                    {availableBranches.map((branch) => (
                      <option key={branch.name} value={branch.name}>
                        {branch.name}
                        {branch.is_default ? t('branchModal.defaultSuffix') : ''}
                      </option>
                    ))}
                  </select>
                )}

                {!loadingBranches && availableBranches.length === 0 && (
                  <input
                    type="text"
                    value={buildBranch}
                    onChange={(e) => setBuildBranch(e.target.value)}
                    placeholder="ex: main, develop, feature/xyz"
                    className="w-full px-4 py-2 border border-border-1 rounded-lg
                             bg-surface-3 text-text-primary
                             placeholder-text-quaternary
                             focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent
                             transition-all"
                  />
                )}

                {branchesError && (
                  <p className="mt-2 text-xs text-amber-400">
                    <ExclamationTriangleIcon className="w-4 h-4 inline mr-1" /> {branchesError}
                  </p>
                )}

                {!branchesError && (
                  <p className="mt-2 text-xs text-text-tertiary">
                    {t('branchModal.defaultBranch', { branch: selectedRepoForBuild.default_branch })}
                  </p>
                )}
              </div>

              {/* Image Tag Input */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  {t('branchModal.imageTag')}
                </label>
                <input
                  type="text"
                  value={buildImageTag}
                  onChange={(e) => setBuildImageTag(e.target.value)}
                  placeholder="latest, v1.0.0, staging..."
                  className="w-full px-4 py-2 border border-border-1 rounded-lg
                           bg-surface-3 text-text-primary
                           placeholder-text-quaternary
                           focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent
                           transition-all"
                />
                <p className="mt-1 text-xs text-text-tertiary">
                  {t('branchModal.imageTagHelp')}
                </p>
              </div>

              {/* Source Subpath for Monorepo */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  {t('branchModal.sourceSubpath')}
                </label>
                <input
                  type="text"
                  value={sourceSubpath}
                  onChange={(e) => setSourceSubpath(e.target.value)}
                  placeholder="ex: apps/backend, packages/api"
                  className="w-full px-4 py-2 border border-border-1 rounded-lg
                           bg-surface-3 text-text-primary
                           placeholder-text-quaternary
                           focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent
                           transition-all"
                />
                <p className="mt-1 text-xs text-text-tertiary">
                  {t('branchModal.sourceSubpathHelp')}
                </p>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowBranchSelectModal(false);
                    setSelectedRepoForBuild(null);
                  }}
                  className="flex-1 px-4 py-2 border border-border-1 rounded-lg
                           text-text-secondary hover:bg-surface-3
                           transition-colors"
                >
                  {t('branchModal.cancel')}
                </button>
                <button
                  onClick={handleConfirmBuild}
                  disabled={buildingRepoId !== null}
                  className="flex-1 px-4 py-2 bg-accent-600 hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed
                           text-white rounded-lg font-medium transition-colors"
                >
                  {buildingRepoId !== null ? t('branchModal.building') : t('branchModal.startBuild')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Progress Modal */}
      <ImportProgressModal
        isOpen={showImportProgress}
        importId={currentImportId}
        onClose={() => setShowImportProgress(false)}
        onSuccess={() => {
          setShowImportProgress(false);
          resetImportForm();
        }}
        onError={() => {
          // Ne pas fermer le modal pour que l'utilisateur puisse voir les logs d'erreur
        }}
      />

      {/* Build Progress Modal */}
      {buildADeployer && buildADeployer.connectedRepositoryId != null && (
        <DeployBuildModal
          isOpen
          buildId={buildADeployer.buildId}
          connectedRepositoryId={buildADeployer.connectedRepositoryId}
          branch={buildADeployer.branch === '-' ? null : buildADeployer.branch}
          imageUrl={buildADeployer.imageUrl}
          imageTag={buildADeployer.imageTag}
          onClose={() => setBuildADeployer(null)}
          onDeployed={() => loadBuildHistory()}
        />
      )}

      <BuildProgressModal
        isOpen={showBuildProgress}
        buildId={currentBuildId}
        onClose={() => { setShowBuildProgress(false); loadBuildHistory(); }}
        onSuccess={(imageUrl, imageTag) => {
          toast.success(`Image construite: ${imageUrl}:${imageTag}`);
          setShowBuildProgress(false);
          loadBuildHistory();
        }}
        onError={(error) => {
          toast.error(`Erreur de build: ${error}`);
          setShowBuildProgress(false);
          loadBuildHistory();
        }}
        onDelete={(buildId) => {
          deleteBuildFromHistory(buildId);
        }}
        onStatusChange={(buildId, status) => {
          updateBuildStatus(buildId, status);
        }}
      />

      {/* Build Configuration Modal */}
      <Modal
        isOpen={showBuildConfigModal}
        onClose={handleCloseBuildConfig}
        title={`Configuration des builds - ${selectedRepoForConfig?.repo_name || ''}`}
        size="xl"
      >
        {selectedRepoForConfig && (
          <div className="p-6">
            <BuildConfigList
              repositoryId={selectedRepoForConfig.id}
              repositoryName={`${selectedRepoForConfig.repo_owner}/${selectedRepoForConfig.repo_name}`}
              onBuildTriggered={() => {
                loadBuildHistory();
              }}
            />
          </div>
        )}
      </Modal>
    </>
  );
};

export default BuildsPage;
